import { type Registry as Services, service } from '@ember/service';
import { compare, isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { Event } from 'lumen/models';
import type { Day } from 'lumen/services/repositories/day';
import { filterEvents } from 'lumen/utils/filter-events';

import type { WeekData } from './calendar-week';
import HotelEventsCalendarsCalendarWeek from './calendar-week';

const MIN_DAY_HEIGHT = 124;
const DAY_HEADER_HEIGHT = 35;
const EVENT_ROW_HEIGHT = 22;

export interface DayEvent {
  date: Dayjs;
  dayNo: number;
  display: boolean;
  event: Event;
  length: number;
  order: number;
  weekNo: number;
}

interface HotelEventsCalendarsCalendarEventsSignature {
  Args: {
    addEvent: (startDate: string) => void;
    containerHeight?: number;
    isLoading: boolean;
    month: string;
    monthMoment: Dayjs;
    periods?: Day[] | null;
    refreshEvents: () => void;
    reservedHeight?: number;
    selectedEventsCategories: string[];
    selectedEventsStatuses: string[];
  };
}

export default class HotelEventsCalendarsCalendarEvents extends Component<HotelEventsCalendarsCalendarEventsSignature> {
  @service declare store: Services['store'];

  weekdays = [...dayjs.weekdays()];

  get containerHeight(): number {
    return this.args.containerHeight ?? 850;
  }

  get eventPositionDict(): { [day: number]: DayEvent[] } {
    const { periods, isLoading } = this.args;

    if (isLoading || !periods?.length) {
      return {};
    }

    const maxEventsPerDay =
      this.store
        .peekAll<Day>('day')
        .map((day) => day.events.length)
        .reduce(
          (maxEvents, nrOfEvents) =>
            maxEvents > nrOfEvents ? maxEvents : nrOfEvents,
          0,
        ) + 2;

    // Build empty position matrix: [week][dayOfWeek][slot]
    const numWeeks = 6; // overprovisioning
    const daysPerWeek = 7;
    const monthPositionMatrix: boolean[][][] = [];

    for (let week = 0; week < numWeeks; week++) {
      const weekMatrix: boolean[][] = [];
      for (let day = 0; day < daysPerWeek; day++) {
        weekMatrix.push(new Array<boolean>(maxEventsPerDay).fill(false));
      }
      monthPositionMatrix.push(weekMatrix);
    }

    const dict: { [day: number]: DayEvent[] } = {};

    for (const day of periods) {
      const { date } = day;

      const monthWeekNo = this.monthWeekNo(date);
      if (monthWeekNo === null) {
        continue;
      }

      const weekPositionMatrix = monthPositionMatrix[monthWeekNo];

      const filteredEvents = filterEvents(
        // @ts-expect-error: Wrong type.
        [...day.events],
        this.args.selectedEventsStatuses,
        this.args.selectedEventsCategories,
      );

      dict[day.integerDate] = this.setMatrixPositionsForDate(
        date,
        weekPositionMatrix,
        filteredEvents,
        monthWeekNo,
      );
    }

    return dict;
  }

  get maxVisibleEvents(): number {
    const reservedHeight = this.args.reservedHeight ?? 0;
    const availableHeight = this.containerHeight - reservedHeight;
    const weekHeight =
      this.numWeeks > 0
        ? Math.max(availableHeight / this.numWeeks, MIN_DAY_HEIGHT)
        : MIN_DAY_HEIGHT;

    // -1 to always leave room for a "more" button
    return Math.floor((weekHeight - DAY_HEADER_HEIGHT) / EVENT_ROW_HEIGHT) - 1;
  }

  get numWeeks(): number {
    return Math.ceil((this.args.periods?.length ?? 0) / 7);
  }

  get weeks(): WeekData[] {
    const periods = this.args.periods;
    if (!periods?.length) {
      return [];
    }

    const result: WeekData[] = [];
    for (let i = 0; i < periods.length; i += 7) {
      const days = periods.slice(i, i + 7);
      const weekNo = i / 7;

      const eventsByDay: { [integerDate: number]: DayEvent[] } = {};
      const displayEvents: DayEvent[] = [];

      for (const day of days) {
        const dayEvents = this.eventPositionDict[day.integerDate] ?? [];
        eventsByDay[day.integerDate] = dayEvents;

        for (const de of dayEvents) {
          if (de.display && de.order < this.maxVisibleEvents) {
            displayEvents.push(de);
          }
        }
      }

      // @ts-expect-error: Wrong type.
      result.push({ days, weekNo, eventsByDay, displayEvents });
    }

    return result;
  }

  constructor(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    owner: any,
    args: HotelEventsCalendarsCalendarEventsSignature['Args'],
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    super(owner, args);

    // Shift weekdays so Monday is first when locale dictates it
    if (dayjs.localeData().firstDayOfWeek() === 1) {
      this.weekdays.push(this.weekdays.shift()!);
    }
  }

  monthWeekNo(date: Dayjs): number | null {
    if (this.args.monthMoment && date) {
      const month = this.args.monthMoment;
      const dateRangeStart = month.startOf('month').startOf('week');

      return date.diff(dateRangeStart, 'week');
    }
    return null;
  }

  normalizeSortProperties(sortProperties: string[]): string[][] {
    return sortProperties.map((property) => {
      const splitProperties = property.split(':');
      const prop = splitProperties[0]!;
      const direction = splitProperties[1] ?? 'asc';

      return [prop, direction];
    });
  }

  setMatrixPositionsForDate(
    calendarDate: Dayjs,
    weekPositionMatrix: (boolean | string)[][] | undefined,
    filteredEvents: Event[],
    monthWeekNo: number,
  ): DayEvent[] {
    if (!weekPositionMatrix || isEmpty(weekPositionMatrix)) {
      return [];
    }
    if (!filteredEvents?.length) {
      return [];
    }

    const sortedEvents = this.sortByNormalizedSortProperties(filteredEvents, [
      'startDateAsUnix: asc',
      'length:desc',
      'isAccepted:desc',
    ]);

    const eventPositionArray: DayEvent[] = [];

    for (const event of sortedEvents) {
      const startOfWeek = 0;
      const endOfWeek = 6;

      const displayEvent =
        event.startDate.isSame(calendarDate, 'day') ||
        calendarDate.weekday() === startOfWeek;
      const length = calendarDate.isSame(event.endDate, 'week')
        ? event.endDate.diff(calendarDate, 'day') + 1
        : endOfWeek - calendarDate.weekday() + 1;
      const dayNo = calendarDate.weekday();

      const alreadyReserved =
        // @ts-expect-error: event.id may be null
        weekPositionMatrix[dayNo]?.indexOf(event.id) !== -1;
      const order = alreadyReserved
        ? // @ts-expect-error: event.id may be null
          weekPositionMatrix[dayNo]?.indexOf(event.id)
        : weekPositionMatrix[dayNo]?.indexOf(false);

      if (!alreadyReserved && order !== undefined) {
        for (let day = dayNo; day < dayNo + length; day++) {
          if (weekPositionMatrix[day]) {
            // @ts-expect-error: Type 'string | null' is not assignable to type 'string | boolean'.
            weekPositionMatrix[day]![order] = event.id;
          }
        }
      }

      eventPositionArray.push({
        weekNo: monthWeekNo,
        length,
        dayNo,
        order: order as number,
        event,
        date: calendarDate,
        display: displayEvent,
      });
    }

    return eventPositionArray.sort((a, b) => a.order - b.order);
  }

  sortByNormalizedSortProperties(
    items: Event[],
    sortProperties: string[],
  ): Event[] {
    if (!items) {
      return [];
    }

    const normalizedSortProperties =
      this.normalizeSortProperties(sortProperties);
    return items.sort((itemA, itemB) => {
      for (const [prop, direction] of normalizedSortProperties) {
        if (prop) {
          const result = compare(
            itemA[prop as keyof Event],
            itemB[prop as keyof Event],
          );
          if (result !== 0) {
            return direction === 'desc' ? -1 * result : result;
          }
        }
      }
      return 0;
    });
  }

  <template>
    <div class="card-tile events-calendar">
      <div class="events-calendar__header">
        {{#each this.weekdays as |weekday|}}
          <div class="events-calendar__header-day">
            {{weekday}}
          </div>
        {{/each}}
      </div>

      {{#each this.weeks as |week|}}
        <HotelEventsCalendarsCalendarWeek
          @addEvent={{@addEvent}}
          @isLoading={{@isLoading}}
          @maxVisibleEvents={{this.maxVisibleEvents}}
          @monthMoment={{@monthMoment}}
          @refreshEvents={{@refreshEvents}}
          @week={{week}}
        />
      {{/each}}
    </div>
  </template>
}
